
import { Vue, Component, Prop } from "vue-property-decorator";

import GET_SUBSCRIPTIONS_DETAILS from "@/apollo/payment/GetSubscriptionsDetails.gql";

import PricePlans from "@/components/payment/prices/PricePlans.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import UiButton from "@/components/button/ui-button.vue";
import FormGroup from "@/components/form/ManagerAddonFormGroup.vue";
import ScrollableContainer from "@/components/common/ScrollableContainer.vue";
import SubscriptionActionModal from "@/components/payment/SubscriptionActionModalForManagerAddon.vue";
import PageHeader from '@/components/page/PageHeader.vue';
import PaymentCardForm from "@/components/payment/PaymentCardForm.vue";
import ModalSectionsDivider from "@/components/modal/ModalSectionsDivider.vue";
import FormError from "@/components/common/FormError.vue";
import CardRequiredModal from "@/components/payment/CardRequiredModal.vue";


import lambdaRequest from "@/helpers/lambdaRequest";

import getActiveManagersCountCondition from "@/helpers/queryConditions/getActiveManagersCountCondition";

import { SubscriptionPlan } from "@/entities/payment/SubscriptionPlan";
import { Company, Subscription, User } from "@/generated/graphql";
import { SubscriptionStatus } from "@/entities/payment/SubscriptionStatus";
import PaymentCardDataChangeEventPayload from "@/entities/PaymentCardDataChangeEventPayload";
import RouteNames from "@/entities/RouteNames";
import ModalContent from "@/components/modal/ModalContent.vue";
import ModalFullScreen from "@/components/modal/ModalFullScreen.vue";

interface SubscriptionDetails {
    subscriptionPlans: SubscriptionPlan[];
    currentSubscription: Subscription;
    activeManagersCount: number;
    paymentCardId?: string;
}

interface CurrentSubscriptionCardAction {
    buttonText: string;
    modalTitle: string;
    action: (subscriptionId: string) => void;
    paymentCardRequired: boolean;
}

interface SubscriptionModalData {
    subscription: SubscriptionPlan;
    title: string;
    action: (subscriptionId: string) => void;
    paymentCardRequired: boolean;
    id: string;
}

@Component({
    components: {
        PricePlans,
        LoadingOverlay,
        UiButton,
        ScrollableContainer,
        SubscriptionActionModal,
        PageHeader,
        PaymentCardForm,
        ModalSectionsDivider,
        FormError,
        CardRequiredModal,
        ModalContent,
        ModalFullScreen,
        FormGroup
    },
    apollo: {
        subscriptionDetails: {
            query: GET_SUBSCRIPTIONS_DETAILS,
            variables() {
                const companyId = this.company.id;
                return {
                    companyId,
                    userId: this.currentUser.id,
                    activeManagersCountCondition: getActiveManagersCountCondition(this.companyId),
                };
            },
            update: (data): SubscriptionDetails => {
                return {
                    subscriptionPlans: data.subscription_plan,
                    currentSubscription: data.subscription[0],
                    activeManagersCount: data.activeManagersCount.aggregate.count,
                    paymentCardId: data.payment_card[0]?.id,
                };
            },
        },
    },
})
export default class ManagerAddonModal extends Vue {
    @Prop({ type: Object, required: true }) readonly company!: Company;
    @Prop({ type: Object, required: true }) readonly currentUser!: User;

    subscriptionDetails: SubscriptionDetails | null = null;
    subscriptionActionModalShown: boolean = false;
    chosenSubscriptionId: string = "";
    processing: boolean = false;
    card: stripe.elements.Element | null = null;
    cardDataValid: boolean = false;
    errorMessage: string = "";
    alertDialogShown: boolean = false;
    selectedSubscriptionPlanId: string = null;
    callSuccessMessage: boolean = false;

    get actionDisabled(): boolean {
        return this.chosenSubscription.paymentCardRequired && !this.cardDataValid;
    }

    get isErrorShown(): boolean {
        return this.errorMessage.length > 0;
    }

    get activeManagersCount(): number {
        return this.subscriptionDetails?.activeManagersCount || 0;
    }

    get chosenSubscription(): SubscriptionModalData {
        const paymentCardRequired = this.currentSubscriptionActionDetails.paymentCardRequired;

        if (this.chosenSubscriptionId === this.currentSubscriptionPlanId) {
            return {
                subscription: this.currentSubscriptionPlan,
                title: this.currentSubscriptionActionDetails.modalTitle,
                action: this.currentSubscriptionActionDetails.action,
                id: this.currentSubscriptionId,
                paymentCardRequired,
            };
        }

        const shownSubscription = this.subscriptionPlans.find(
            (subscription) => subscription.id === this.chosenSubscriptionId
        );

        return {
            subscription: shownSubscription!,
            title: this.isTrialPeriod ? "Select plan" : "Purchase plan",
            action: this.subscriptionAction,
            id: this.chosenSubscriptionId,
            paymentCardRequired: !this.isTrialPeriod && this.paymentRequired,
        };
    }

    get currentCompanySubscription(): Subscription | undefined {
        return this.company.subscriptions[0];
    }

    get isTrialPeriod(): boolean {
        return this.currentCompanySubscription?.status === SubscriptionStatus.TRIALING;
    }

    get paymentRequired(): boolean {
        return !this.currentUser.subscription_required && !Boolean(this.subscriptionDetails?.paymentCardId) || !Boolean(this.subscriptionDetails?.paymentCardId);
    }

    get currentSubscriptionPlanId(): string {
        return this.currentSubscription?.subscription_plan_id;
    }

    get currentSubscriptionId(): string {
        return this.currentSubscription.id;
    }

    get subscriptionPlans(): SubscriptionPlan[] {
        const allSubscriptionPlans = this.subscriptionDetails?.subscriptionPlans ?? [];
        const specificSubscriptionPlans = allSubscriptionPlans.filter(plan => {
            return plan.inner_name === 'PREMIUMPLUSFIVE' || plan.inner_name === 'PREMIUMPLUSTEN' || plan.inner_name === 'PREMIUMPLUSFIFTEEN' || plan.inner_name === 'PREMIUMPLUSTWENTY' || plan.inner_name === 'PREMIUMPLUSTWENTYFIVE' || plan.inner_name === 'PREMIUMPLUSTHIRTY' || plan.inner_name === "PREMIUMPLUSTHIRTYFIVE" || plan.inner_name === "PREMIUMPLUSFORTY" || plan.inner_name === "PREMIUMPLUSFORTYFIVE" || plan.inner_name === "PREMIUMPLUSFIFTY";
        });

        return specificSubscriptionPlans;
    }

    get sortedSubscriptionPlans() {
        return this.subscriptionPlans.slice().sort((a, b) => {
            const innerNameA = a.subscription_plan_access_permission?.managers_count;
            const innerNameB = b.subscription_plan_access_permission?.managers_count;
            if (innerNameA < innerNameB) return -1;
            if (innerNameA > innerNameB) return 1;
            return 0;
        });
    }

    isOptionDisabled(plan) {
        if (
            this.currentSubscriptionPlan &&
            plan.subscription_plan_access_permission.managers_count <= this.currentSubscriptionPlan.subscription_plan_access_permission.managers_count
        ) {
            return true;
        }
        return false;
    }

    get managersPrice(): string {
        const selectedPlan = this.subscriptionPlans.find(
            (plan) => plan.id === this.selectedSubscriptionPlanId
        );

        if (selectedPlan) {
            const newPrice = Number(selectedPlan.price) / 100;
            return `$${(newPrice)}`;
        }

        return "";
    }

    get getSelectedNumberOfManagers(): string {
        const selectedPlan = this.subscriptionPlans.find(
            (plan) => plan.id === this.selectedSubscriptionPlanId
        );

        if (selectedPlan) {
            const numberOfManagers = selectedPlan.inner_name;
            return numberOfManagers === 'PREMIUMPLUSFIVE' ? '5' : numberOfManagers === 'PREMIUMPLUSTEN' ? '10' : numberOfManagers === 'PREMIUMPLUSFIFTEEN' ? '15' : numberOfManagers === 'PREMIUMPLUSTWENTY' ? '20' : numberOfManagers === 'PREMIUMPLUSTWENTYFIVE' ? '25' : numberOfManagers === 'PREMIUMPLUSTHIRTY' ? '30' : numberOfManagers === 'PREMIUMPLUSTHIRTYFIVE' ? '35' : numberOfManagers === 'PREMIUMPLUSFORTY' ? '40' : numberOfManagers === 'PREMIUMPLUSFORTYFIVE' ? '45' : numberOfManagers === 'PREMIUMPLUSFIFTY' ? '50' : '';
        }

        return "";
    }

    get currentSubscription(): Subscription {
        return this.subscriptionDetails!.currentSubscription;
    }
    get getBillCurrentPeriodEndDate(): string {
        const periodEndTime = this.subscriptionDetails!.currentSubscription!.current_period_end;
        const formatedDate = new Date(periodEndTime * 1000);
        return formatedDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }

    get currentSubscriptionPlan(): SubscriptionPlan {
        return this.currentSubscription?.subscription_plan as SubscriptionPlan;
    }

    get isLoading(): boolean {
        return this.$apollo.queries.subscriptionDetails.loading;
    }

    get subscriptionPlansExist(): boolean {
        return this.subscriptionPlans.length > 0;
    }

    get subscriptionAction(): (subscriptionId: string) => void {
        if (this.paymentRequired && !this.isTrialPeriod) {
            return this.subscribeWithNewCard;
        }

        if (this.currentSubscription) {
            return this.subscribeToAnotherPlan;
        }

        return this.subscribeWithExistingCard;
    }

    get paymentCardAdded(): boolean {
        return Boolean(this.currentSubscription.payment_card_id);
    }

    get currentSubscriptionActionDetails(): CurrentSubscriptionCardAction {
        const cancelSubscription = "Cancel";
        const cancelAtPeriodEnd = this.currentSubscription?.cancel_at_period_end;

        if (cancelAtPeriodEnd) {
            return {
                buttonText: "Activate",
                action: this.restoreSubscription,
                modalTitle: "Renew this subscription?",
                paymentCardRequired: false,
            };
        }

        if (this.currentSubscription?.status === SubscriptionStatus.TRIALING) {
            return {
                buttonText: cancelSubscription,
                action: this.cancelSubscription,
                modalTitle: "Cancel your trial subscription?",
                paymentCardRequired: false,
            };
        }

        if (this.currentSubscription?.status === SubscriptionStatus.PAST_DUE) {
            return {
                buttonText: cancelSubscription,
                action: this.cancelSubscriptionPermanently,
                modalTitle: "Cancel your subscription permanently?",
                paymentCardRequired: false,
            };
        }

        return {
            buttonText: cancelSubscription,
            action: this.cancelSubscription,
            modalTitle: "Cancel your current subscription?",
            paymentCardRequired: false,
        };
    }

    async subscribeWithExistingCard(id: string): Promise<void> {
        await lambdaRequest({
            lambdaName: "subscribeWithExistingCard",
            requestData: {
                card_id: this.subscriptionDetails!.paymentCardId!,
                user_id: this.currentUser.id,
                subscription_plan_id: id
            },
        });
        this.$emit("refetch");
    }

    async subscribeWithNewCard(id: string): Promise<void> {
        if (!this.card) {
            return;
        }

        const { token, error } = await this.$stripe.createToken(this.card);

        if (error) {
            throw error;
        }

        await lambdaRequest({
            lambdaName: "subscribeWithNewCard",
            requestData: {
                token: token!.id,
                user_id: this.currentUser.id,
                subscription_plan_id: id
            },
        });
        this.$emit("refetch");
    }

    async cancelSubscription(id: string): Promise<void> {
        await lambdaRequest({
            lambdaName: "cancelSubscription",
            requestData: {
                subscription_id: id,
            },
        });
    }

    onPaymentCardDataChange(payload: PaymentCardDataChangeEventPayload): void {
        const { complete, card } = payload;
        this.card = card;
        this.cardDataValid = complete;
    }

    async cancelSubscriptionPermanently(id: string): Promise<void> {
        await lambdaRequest({
            lambdaName: "cancelSubscriptionPermanently",
            requestData: {
                subscription_id: id,
                company_id: this.company.id,
            },
        });
    }

    async restoreSubscription(id: string): Promise<void> {
        await lambdaRequest({
            lambdaName: "restoreSubscription",
            requestData: {
                subscription_id: id,
            },
        });
    }

    async subscribeToAnotherPlan(subscriptionPlanId: string): Promise<void> {
        await lambdaRequest({
            lambdaName: "subscribeToAnotherPlan",
            requestData: {
                company_id: this.company.id,
                subscription_plan_id: subscriptionPlanId,
                current_subscription_id: this.currentSubscriptionId,
            },
        });
    }

    async submitAction(): Promise<void> {
        this.errorMessage = "";
        this.processing = true;

        try {
            await this.chosenSubscription.action(this.chosenSubscription.id);
            this.subscriptionActionModalShown = false;
            this.callSuccessMessage = true;
            await this.$apollo.queries.subscriptionDetails.refetch();
        } catch (error) {
            const message = error.message;
            if (message) {
                this.errorMessage = message;
            }
        } finally {
            this.processing = false;
        }
    }

    isCurrentSubscription(subscriptionPlanId: string, currentSubscriptionId: string): boolean {
        return subscriptionPlanId === currentSubscriptionId;
    }

    onSubscriptionActionModalClose(): void {
        this.subscriptionActionModalShown = false;
        this.chosenSubscriptionId = "";
        this.selectedSubscriptionPlanId = "";
        this.errorMessage = "";
        this.$emit("openManagerAddonModalAndCloseSubscriptionActionModalClose");
    }
    onClosingModalSuccessModal(): void {
        location.reload();
    }
    // onSuccessConfirmation(): void {
    //     this.
    // }


    handlePlanSelection(subscriptionId: string) {
        const currentSubscription = subscriptionId === this.currentSubscription?.subscription_plan_id;
        const cancelAtPeriodEnd = this.currentSubscription?.cancel_at_period_end;

        if (currentSubscription && cancelAtPeriodEnd && this.isTrialPeriod && !this.paymentCardAdded) {
            this.alertDialogShown = true;
            return;
        }

        this.chosenSubscriptionId = subscriptionId;
        this.subscriptionActionModalShown = true;
    }
    showSubscriptionActionModal(selectedSubscriptionPlanId: string): void {
        const currentSubscription = selectedSubscriptionPlanId === this.currentSubscription?.subscription_plan_id;
        const cancelAtPeriodEnd = this.currentSubscription?.cancel_at_period_end;

        if (currentSubscription && cancelAtPeriodEnd && this.isTrialPeriod && !this.paymentCardAdded) {
            this.alertDialogShown = true;
            return;
        }

        this.chosenSubscriptionId = selectedSubscriptionPlanId;
        this.subscriptionActionModalShown = true;
    }


    redirectToAccountSettings(): void {
        this.$router.push({
            name: RouteNames.USER_PROFILE,
        })
    }


    closeModal(): void {
        this.$emit("closeManagerAddonModal");
    }
}
